import { type WorkspaceTable } from "@/components/Workspace";
import { getSavedWorkspaces, saveWorkspace } from "@/features/workspaces/save-workspace/SaveWorkspaceHandler";
import { defineStore } from "pinia";
import { Ref, ref } from "vue";
import i18n from "@/i18n";
import { TableView } from "@/components/ColumnConfigurator";
import { Column } from "@/components/Workspace";
import { FEATURE_NAMES } from "@/loggers/types";
import { logError } from "@/loggers";
import { ROUTES, TRACK_AND_TRACE_WORKSPACE_VIEWS } from "@/router/constants";
import { CUSTOMER_FEATURE_FLAG_NAME } from "@/global";
import { useUserStore } from "@/store/user/user";

export const useWorkspaceStore = defineStore("workspace", () => {
  const workspaceTable: Ref<WorkspaceTable | null> = ref(null);
  const workspaces = ref([]);
  const fetching = ref<boolean>(false);
  const isInitialized = ref<boolean>(false);
  const workspaceErrorMessage = ref<string>("");
  const workspaceView = ref<TableView>("custom");
  const configuredColumns = ref<Column[]>([]);

  const setWorkspaceTable = (newWorkspaceTable: WorkspaceTable) => {
    workspaceTable.value = newWorkspaceTable;
  };

  const setWorkspaceView = (newWorkspaceView: TableView) => {
    workspaceView.value = newWorkspaceView;
  };

  const setConfiguredColumns = (newConfiguredColumns: Column[]) => {
    configuredColumns.value = newConfiguredColumns;
  };

  const generateWorkSpaceQueryString = (workspace: any) => {
    // Generate an array of query objects, allowing multiple entries with the same id but unique value, since we have widget filters depending on this in dnd.
    const queryParts: Array<{ id: string; value: any }> = [];
    if (workspace.workspaceTab && workspace.workspaceTab !== "null") {
      queryParts.push({ id: "tab", value: workspace.workspaceTab });
    }

    if (getWorkspaceType(workspace.type) === "demurrageAndDetention" && Array.isArray(workspace.data)) {
      const statusItems = workspace.data.filter((item) => item.id === "status");
      statusItems.forEach((item) => {
        queryParts.push({ id: item.id, value: item.value });
      });
    }
    workspace.data?.forEach((item) => {
      if (
        item?.id &&
        item?.id !== "user-columns" &&
        item?.value !== undefined &&
        !queryParts.some((part) => part.id === item.id && part.value === item.value)
      ) {
        queryParts.push({ id: item.id, value: item.value });
      }
    });

    return "?" + queryParts.map((part) => `${encodeURIComponent(part.id)}=${part.value}`).join("&");
  };
  const getWorkspaces = async () => {
    if (isInitialized.value) {
      return workspaceTable.value;
    }

    fetching.value = true;
    try {
      const { filters, userColumns, defaultColumns, preConfiguredColumns } = await getSavedWorkspaces();

      const updatedFilters = filters.map((workspace) => {
        const workspaceView = getWorkspaceType(workspace.type);

        return {
          ...workspace,
          routePath: `${getWorkspaceBasePath(workspaceView)}/${encodeURIComponent(workspace.name)}/${workspaceView}`,
          query: generateWorkSpaceQueryString(workspace)
        };
      });
      workspaceTable.value = { userColumns, defaultColumns, preConfiguredColumns, userFilters: updatedFilters };
      isInitialized.value = true;
    } catch (error) {
      workspaceTable.value = {
        userColumns: [],
        defaultColumns: [],
        userFilters: [],
        preConfiguredColumns: []
      } as WorkspaceTable;
      workspaceErrorMessage.value = i18n.global.t("ERROR_LOADING_WORKSPACE");
      logError(FEATURE_NAMES.MVS_WORKSPACES, `Error loading workspace: ${error}`);
    } finally {
      fetching.value = false;
    }
    return workspaceTable.value;
  };

  const addWorkspace = async (workspacePayload) => {
    fetching.value = true;
    try {
      const existingWorkspacesToBeSaved = workspaceTable.value.userFilters.map(({ ...workspace }) => workspace);
      const workspacesToBeSaved = [...existingWorkspacesToBeSaved, workspacePayload];
      await saveWorkspace(workspacesToBeSaved);
      isInitialized.value = false;
      await getWorkspaces();
    } catch (error) {
      workspaceErrorMessage.value = i18n.global.t("ERROR_SAVING_WORKSPACE");
      logError(
        FEATURE_NAMES.MVS_WORKSPACES,
        `Error saving workspace:[${workspacePayload.name}][${workspacePayload.type}] ${error}`
      );
      throw error;
    } finally {
      fetching.value = false;
    }
  };

  const removeWorkspace = async (workspaceId) => {
    fetching.value = true;
    try {
      const updatedWorkspaces = workspaceTable.value.userFilters.filter((workspace) => workspace.id !== workspaceId);
      await saveWorkspace(updatedWorkspaces);

      workspaceTable.value.userFilters = updatedWorkspaces;
    } catch (error) {
      workspaceErrorMessage.value = i18n.global.t("ERROR_DELETING_WORKSPACE");
      logError(FEATURE_NAMES.MVS_WORKSPACES, `Error deleting workspace [${workspaceId}]: ${error}`);
    } finally {
      fetching.value = false;
    }
  };

  // Shipments and orders workspaces live inside the Track & Trace page for customers on that flag.
  const getWorkspaceBasePath = (workspaceView: string) => {
    const { isCustomerFeatureFlagEnabled } = useUserStore();
    const isTrackAndTrace =
      TRACK_AND_TRACE_WORKSPACE_VIEWS.includes(workspaceView) &&
      isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_TRACK_AND_TRACE_VIEW);

    return isTrackAndTrace ? ROUTES.TRACK_AND_TRACE_WORKSPACE : ROUTES.WORKSPACE;
  };

  const getWorkspaceType = (workspaceType: string) => {
    if (["dndNew", "dndImport", "demurrage-and-detention"].includes(workspaceType)) {
      return "demurrageAndDetention";
    }
    return workspaceType;
  };

  const ensureWorkspaceTable = async () => {
    if (!isInitialized.value || !workspaceTable.value) {
      await getWorkspaces();
    }
    return workspaceTable.value;
  };

  return {
    workspaceTable,
    workspaces,
    fetching,
    workspaceErrorMessage,
    setWorkspaceTable,
    generateWorkSpaceQueryString,
    getWorkspaces,
    addWorkspace,
    removeWorkspace,
    workspaceView,
    setWorkspaceView,
    setConfiguredColumns,
    configuredColumns,
    ensureWorkspaceTable
  };
});
